/**
 * Credit Memo Status Constants and Utilities
 *
 * This file defines the valid credit memo statuses and their display properties.
 * These MUST match the STATUS_CHOICES in the backend (api/accounts/models.py).
 *
 * Backend statuses: draft, awaiting_allocation, allocated, cancelled
 */

export const CREDIT_MEMO_STATUSES = ['draft', 'awaiting_allocation', 'allocated', 'cancelled'] as const;

export type CreditMemoStatus = typeof CREDIT_MEMO_STATUSES[number];

export interface CreditMemoStatusConfig {
  label: string;
  textColorClass: string;
}

/**
 * Status display configuration
 * - draft: muted gray
 * - awaiting_allocation: amber warning
 * - allocated: green success
 * - cancelled: red error
 */
export const CREDIT_MEMO_STATUS_CONFIG: Record<CreditMemoStatus, CreditMemoStatusConfig> = {
  draft: {
    label: 'Draft',
    textColorClass: 'text-muted-foreground',
  },
  awaiting_allocation: {
    label: 'Awaiting Allocation',
    textColorClass: 'text-amber-600 dark:text-amber-400',
  },
  allocated: {
    label: 'Allocated',
    textColorClass: 'text-green-600 dark:text-green-400',
  },
  cancelled: {
    label: 'Cancelled',
    textColorClass: 'text-red-600 dark:text-red-400',
  },
};

/**
 * Validates that a status string is a valid credit memo status.
 * Throws a descriptive error if the status is invalid.
 */
export function validateCreditMemoStatus(status: string, context?: string): CreditMemoStatus {
  if (!CREDIT_MEMO_STATUSES.includes(status as CreditMemoStatus)) {
    const validStatuses = CREDIT_MEMO_STATUSES.join(', ');
    const contextStr = context ? ` for ${context}` : '';
    throw new Error(
      `Invalid credit memo status "${status}"${contextStr}. ` +
      `Valid statuses are: ${validStatuses}. ` +
      `This may indicate a mismatch between frontend and backend status definitions.`
    );
  }
  return status as CreditMemoStatus;
}

/**
 * Gets the display configuration for a credit memo status.
 * Validates the status and throws a descriptive error if invalid.
 */
export function getCreditMemoStatusConfig(status: string, context?: string): CreditMemoStatusConfig {
  const validatedStatus = validateCreditMemoStatus(status, context);
  return CREDIT_MEMO_STATUS_CONFIG[validatedStatus];
}

/**
 * Checks if a status is valid without throwing.
 * Useful for conditional rendering or filtering.
 */
export function isValidCreditMemoStatus(status: string): status is CreditMemoStatus {
  return CREDIT_MEMO_STATUSES.includes(status as CreditMemoStatus);
}
